import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Link, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import CacheBuster from "./components/CacheBuster.jsx";
// Test change for bugbot analysis

import OfflineBanner from "./components/OfflineBanner";
import StatusBarStrip from "./components/StatusBarStrip";
import { useAuth } from "./AuthContext";
import useUserProfile from "./hooks/useUserProfile";
import useCoopMemberRole from "./hooks/useCoopMemberRole";
import useDeliveryRole from "./hooks/useDeliveryRole";
import usePackerRole from "./hooks/usePackerRole";
import useAffiliate from "./hooks/useAffiliate";
import RiderSubMenu from "./components/RiderSubMenu";
import useAdminPasswordAccess from "./hooks/useAdminPasswordAccess";
import AdminPasswordModal from "./components/AdminPasswordModal";
import { useAffiliateTracking } from "./hooks/useAffiliateTracking";
import { handleGoogleRedirectResult, db, auth } from "@/lib/firebase";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { generateMemberId } from "@/lib/memberId";

import ProtectedRoute from "./components/ProtectedRoute.jsx";
import RequireFullMemberOrRole from "./components/RequireFullMemberOrRole.jsx";
import GuestRoute from "./components/GuestRoute.jsx";
import RequireRole from "./components/RequireRole.jsx";
import LoginModal from "./components/LoginModal.jsx";
import BecomeMember from "./pages/BecomeMember.jsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.jsx";
import TermsOfService from "./pages/TermsOfService.jsx";
import AccountDeletion from "./pages/AccountDeletion.jsx";
import AffiliatePage from "./pages/AffiliatePage.jsx";
import { CartProvider } from "@/contexts/CartContext.jsx";
import { DeliveryModeProvider } from "@/contexts/DeliveryModeContext.jsx";
import DeliveryModeToggle from "@/components/DeliveryModeToggle.jsx";
import DeliveryModeBanner from "@/components/DeliveryModeBanner.jsx";
import DeliveryModeRouteGuard from "@/components/DeliveryModeRouteGuard.jsx";
import { useDeliveryMode } from "@/contexts/DeliveryModeContext.jsx";
// Lazy load heavy admin components
const PromotionManagement = lazy(() => import("./pages/admin/PromotionManagement.jsx"));
const ProductAffiliateSettings = lazy(() => import("./components/admin/ProductAffiliateSettings.jsx"));
const StoreManager = lazy(() => import("./pages/StoreManager.jsx"));

const CooperativeStoreManager = lazy(() => import("./components/store/CooperativeStoreManager.jsx"));
const DeliveriesAdmin = lazy(() => import("./pages/admin/Deliveries.jsx"));
const DeliveryIssues = lazy(() => import("./pages/admin/DeliveryIssues.jsx"));
const DeliverySettings = lazy(() => import("./pages/admin/DeliverySettings.jsx"));

// Services Marketplace components
const ServiceMarketplace = lazy(() => import("./pages/services/ServiceMarketplace.jsx"));

import ppacLogo from "./assets/ppac-logo.png";
import ToastProvider from "./components/ToastProvider.jsx";
import MobileBackButton from "./components/MobileBackButton.jsx";
import MobileTabBar from "./components/MobileTabBar.jsx";
import NotificationModal from "./components/NotificationModal.jsx";
import A2HSPrompt from "./components/A2HSPrompt.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import PerformanceDashboard from "./components/PerformanceDashboard.jsx";
import PerformanceMonitor from "./components/PerformanceMonitor.jsx";
import ResponsiveNavigation, { NavItem } from "./components/ResponsiveNavigation.jsx";
import { initMessaging } from "@/lib/firebase";
import { initializeBundleOptimizations } from "@/utils/bundleOptimizations.jsx";
import { prefetchCommonData as prefetchFirestoreData } from "@/utils/firestoreOptimizations";
import { initializeSecurityOptimizations } from "@/utils/securityOptimizations";
import { fcmService } from "@/services/FCMService";

// Accounting Route Guard Component
function AccountingRouteGuard({ coopRole, hasAccountingAccess, onShowPasswordModal }) {
  const [hasShownModal, setHasShownModal] = React.useState(false);

  React.useEffect(() => {
    // Check if user is officer OR admin with password access
    const isOfficer = ["treasurer", "manager", "general manager"].includes(coopRole);
    const isAdminWithAccess = coopRole === 'admin' && hasAccountingAccess;
    
    if (!isOfficer && !isAdminWithAccess && coopRole === 'admin' && !hasShownModal) {
      // Admin without password access - show password modal once
      setHasShownModal(true);
      onShowPasswordModal();
    }
  }, [coopRole, hasAccountingAccess, onShowPasswordModal, hasShownModal]);

  // Check if user is officer OR admin with password access
  const isOfficer = ["treasurer", "manager", "general manager"].includes(coopRole);
  const isAdminWithAccess = coopRole === 'admin' && hasAccountingAccess;
  
  if (isOfficer || isAdminWithAccess) {
    return (
      <Suspense fallback={<div className="p-6">Loading…</div>}>
        <Accounting />
      </Suspense>
    );
  } else if (coopRole === 'admin') {
    // Admin without password access - redirect to home
    return <Navigate to="/" replace />;
  } else {
    // Not authorized - redirect to home
    return <Navigate to="/" replace />;
  }
}

// Retry wrapper: if a lazy chunk fails to load (often after a fresh deploy), hard-reload to get the new build
function lazyWithRetry(importer) {
  return React.lazy(() =>
    importer().catch((err) => {
      // Lazy loading error - component will show error boundary
      throw err;
    })
  );
}

const preloadHome = () => import("./pages/Home.jsx");
const Home = lazyWithRetry(preloadHome);
const preloadSignup = () => import("./pages/Signup.jsx");
const Signup = lazyWithRetry(preloadSignup);
const preloadDashboard = () => import("./pages/Dashboard.jsx");
const Dashboard = lazyWithRetry(preloadDashboard);
const preloadVerify = () => import("./pages/Verify.jsx");
const Verify = lazyWithRetry(preloadVerify);
const preloadVerifyAction = () => import("./pages/VerifyAction.jsx");
const VerifyAction = lazyWithRetry(preloadVerifyAction);
// OAuthHandler for server-side Google OAuth callback handling
const preloadOAuthHandler = () => import("./pages/OAuthHandler.jsx");
const OAuthHandler = lazyWithRetry(preloadOAuthHandler);
const preloadMobileDashboardLogin = () => import("./pages/MobileDashboardLogin.jsx");
const MobileDashboardLogin = lazyWithRetry(preloadMobileDashboardLogin);
const preloadReset = () => import("./pages/Reset.jsx");
const Reset = lazyWithRetry(preloadReset);
const preloadProfile = () => import("./pages/Profile.jsx");
const Profile = lazyWithRetry(preloadProfile);
const preloadOrdersPage = () => import("./pages/Orders.jsx");
const OrdersPage = lazyWithRetry(preloadOrdersPage);

const preloadRequirements = () => import("./pages/RequirementsMembership.jsx");
const RequirementsMembership = lazyWithRetry(preloadRequirements);


const preloadAdminLayout = () => import("./pages/Admin.jsx");
const AdminLayout = lazyWithRetry(preloadAdminLayout);
const preloadAdminUsers = () => import("./pages/admin/Users.jsx");
const AdminUsers = lazyWithRetry(preloadAdminUsers);
const preloadEditHome = () => import("./pages/admin/EditHome.jsx");
const EditHome = lazyWithRetry(preloadEditHome);
const preloadMembershipReview = () => import("./pages/admin/MembershipReview.jsx");
const MembershipReview = lazyWithRetry(preloadMembershipReview);

const preloadMemberPayments = () => import("./pages/admin/MemberPayments.jsx");
const MemberPayments = lazyWithRetry(preloadMemberPayments);
const preloadStorePayments = () => import("./pages/admin/StorePayments.jsx");
const StorePayments = lazyWithRetry(preloadStorePayments);
const preloadAccountingSettings = () => import("./pages/admin/AccountingSettings.jsx");
const AccountingSettingsPage = lazyWithRetry(preloadAccountingSettings);
const preloadOps = () => import("./pages/admin/OpsDashboard.jsx");
const OpsDashboard = lazyWithRetry(preloadOps);
const preloadCoopMembership = () => import("./pages/admin/CoopMembership.jsx");
const CoopMembership = lazyWithRetry(preloadCoopMembership);
const preloadStoreManagement = () => import("./pages/admin/StoreManagement.jsx");
const StoreManagementPage = lazyWithRetry(preloadStoreManagement);
const preloadServiceManagement = () => import("./pages/admin/ServiceManagement.jsx");
const ServiceManagement = lazyWithRetry(preloadServiceManagement);
const preloadStoreApplications = () => import("./pages/admin/StoreApplications.jsx");
const StoreApplications = lazyWithRetry(preloadStoreApplications);
const preloadStoreOrdersManagement = () => import("./pages/admin/StoreOrdersManagement.jsx");
const StoreOrdersManagement = lazyWithRetry(preloadStoreOrdersManagement);
const preloadStoreApplication = () => import("./pages/StoreApplication.jsx");
const StoreApplication = lazyWithRetry(preloadStoreApplication);
const preloadFCMMessaging = () => import("./pages/admin/FCMMessaging.jsx");
const FCMMessaging = lazyWithRetry(preloadFCMMessaging);
const preloadMembershipApplication = () => import("./pages/MembershipApplication.jsx");
const MembershipApplication = lazyWithRetry(preloadMembershipApplication);
// legacy Payments page removed
const preloadReceiptPage = () => import("./pages/Receipt.jsx");
const ReceiptPage = lazyWithRetry(preloadReceiptPage);

function DashboardRouteElement() {
  const location = useLocation();
  const hasIdToken = React.useMemo(() => {
    if (!location.search) return false;
    const params = new URLSearchParams(location.search);
    return params.has("idToken") || params.has("id_token");
  }, [location.search]);

  if (hasIdToken) {
    return <MobileDashboardLogin />;
  }

  return (
    <ProtectedRoute>
      <Dashboard />
    </ProtectedRoute>
  );
}
// removed gateway result routes
const preloadStores = () => import("./pages/store/Stores.jsx");
const Stores = lazyWithRetry(preloadStores);

const preloadProductDetail = () => import("./pages/store/ProductDetail.jsx");
const ProductDetail = lazyWithRetry(preloadProductDetail);
const preloadDownloadApp = () => import("./pages/DownloadApp.jsx");
const DownloadApp = lazyWithRetry(preloadDownloadApp);
const preloadCooperativeStore = () => import("./pages/store/CooperativeStore.jsx");
const CooperativeStore = lazyWithRetry(preloadCooperativeStore);
const preloadCart = () => import("./pages/store/CartPage.jsx");
const CartPage = lazyWithRetry(preloadCart);
const preloadCheckout = () => import("./pages/store/Checkout.jsx");
const Checkout = lazyWithRetry(preloadCheckout);

// Lazy load accounting pages
const preloadAccounting = () => import("./pages/accounting/index.jsx");
const Accounting = lazyWithRetry(preloadAccounting);
const preloadChart = () => import("./pages/accounting/ChartOfAccounts.jsx");
const ChartOfAccounts = lazyWithRetry(preloadChart);
const preloadJournalEntries = () => import("./pages/accounting/JournalEntries.jsx");
const JournalEntries = lazyWithRetry(preloadJournalEntries);
const preloadGeneralJournal = () => import("./pages/accounting/GeneralJournal.jsx");
const GeneralJournal = lazyWithRetry(preloadGeneralJournal);
const preloadLedger = () => import("./pages/accounting/Ledger.jsx");
const Ledger = lazyWithRetry(preloadLedger);
const preloadTrialBalance = () => import("./pages/accounting/TrialBalance.jsx");
const TrialBalance = lazyWithRetry(preloadTrialBalance);
const preloadFinancialStatements = () => import("./pages/accounting/FinancialStatements.jsx");
const FinancialStatements = lazyWithRetry(preloadFinancialStatements);

// Lazy load new admin pages
const preloadDiscountManagement = () => import("./pages/admin/DiscountManagement.jsx");
const DiscountManagement = lazyWithRetry(preloadDiscountManagement);
const preloadDeliveryManagement = () => import("./pages/admin/DeliveryManagement.jsx");
const DeliveryManagement = lazyWithRetry(preloadDeliveryManagement);
const preloadRiderWallets = () => import("./pages/admin/RiderWallets.jsx");
const RiderWallets = lazyWithRetry(preloadRiderWallets);
const preloadAdminRiderAnalytics = () => import("./pages/admin/RiderAnalytics.jsx");
const AdminRiderAnalytics = lazyWithRetry(preloadAdminRiderAnalytics);
const preloadAPKManagement = () => import("./pages/admin/APKManagement.jsx");
const APKManagement = lazyWithRetry(preloadAPKManagement);
const preloadDailyCooperativeStoreSales = () => import("./pages/admin/DailyCooperativeStoreSales.jsx");
const DailyCooperativeStoreSales = lazyWithRetry(preloadDailyCooperativeStoreSales);
const preloadQRCodeManagement = () => import("./pages/admin/QRCodeManagement.jsx");
const QRCodeManagement = lazyWithRetry(preloadQRCodeManagement);
const preloadStoreSettings = () => import("./pages/admin/StoreSettings.jsx");
const StoreSettings = lazyWithRetry(preloadStoreSettings);
const preloadStoreWallet = () => import("./pages/StoreWallet.jsx");
const StoreWallet = lazyWithRetry(preloadStoreWallet);
const preloadCashOutManagement = () => import("./pages/admin/CashOutManagement.jsx");
const CashOutManagement = lazyWithRetry(preloadCashOutManagement);

// NEW: lazy load reports pages
const preloadReports = () => import("./pages/reports/Reports.jsx");
const Reports = lazyWithRetry(preloadReports);
const preloadReportView = () => import("./pages/reports/ReportView.jsx");
const ReportView = lazyWithRetry(preloadReportView);

// Rider jobs menu (layout + pages)
const preloadRiderLayout = () => import("./pages/rider/Layout.jsx");
const RiderLayout = lazyWithRetry(preloadRiderLayout);
const preloadJobClaim = () => import("./pages/rider/JobClaim.jsx");
const JobClaim = lazyWithRetry(preloadJobClaim);
const preloadMyJobs = () => import("./pages/rider/MyJobs.jsx");
const MyJobs = lazyWithRetry(preloadMyJobs);
const preloadRiderWallet = () => import("./pages/rider/Wallet.jsx");
const RiderWallet = lazyWithRetry(preloadRiderWallet);
const preloadRiderAnalytics = () => import("./pages/rider/Analytics.jsx");
const RiderAnalytics = lazyWithRetry(preloadRiderAnalytics);

const preloadTreasurerPayments = () => import("./pages/TreasurerPayments.jsx");
const TreasurerPayments = lazyWithRetry(preloadTreasurerPayments);

// Order Processing page
const preloadOrderProcessing = () => import("./pages/OrderProcessing.jsx");
const OrderProcessing = lazyWithRetry(preloadOrderProcessing);

// NavItem moved to ResponsiveNavigation.jsx

// Helper function to build display name from profile data
function buildDisplayName(profile) {
  if (!profile) return '';
  
  // First try to construct from firstName, middleName, lastName
  const first = String(profile.firstName || "").trim();
  const mid = String(profile.middleName || "").trim();
  const last = String(profile.lastName || "").trim();
  
  if (first && last) {
    const mi = mid ? `${mid[0].toUpperCase()}.` : "";
    return [first, mi, last].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  }
  
  // Fallback to displayName if available
  if (profile.displayName) {
    return profile.displayName;
  }
  
  // Fallback to email
  if (profile.email) {
    return profile.email;
  }
  
  // Last resort
  return "User";
}

// MobileMenu component for mobile drawer navigation
function MobileMenu({
  open,
  onClose,
  isAdmin,
  isDelivery,
  isTreasurer,
  isManager,
  isStoreManager,
  isPacker,
  isDeliveryOnly,
  notSuspended,
  profile,
  onLogin,
  onSignup,
  onSignout,
  coopRole,
  clearAllAccess,
  isDeliveryMode,
}) {
  React.useEffect(() => {
    // lock scroll
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] md:hidden" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={onClose} />

      {/* Drawer */}
      <div
        className="
          absolute right-0 top-0 h-full w-[82vw] max-w-[320px]
          bg-white shadow-2xl
          pt-[max(env(safe-area-inset-top),0.75rem)] pb-[max(env(safe-area-inset-bottom),0.75rem)]
          flex flex-col
        "
      >
        <div className="px-4 pb-3 flex items-center justify-between">
          <span className="font-semibold">Menu</span>
          <button className="p-2 rounded hover:bg-gray-100" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <nav className="px-2 flex-1 overflow-auto">
          <ul className="space-y-1">
            <li>
              <Link className={`block px-3 py-2 rounded hover:bg-brand-50 ${isDeliveryMode ? 'text-gray-400 cursor-not-allowed opacity-50' : ''}`} to={isDeliveryMode ? '#' : "/"} onClick={isDeliveryMode ? undefined : onClose}>
                Home
              </Link>
            </li>

            {/* Delivery Mode Toggle */}
            <li className="px-3 py-2">
              <DeliveryModeToggle />
            </li>

            {!profile && (
              <>
                <li>
                  <button
                    className="block w-full text-left px-3 py-2 rounded hover:bg-brand-50"
                    onClick={() => {
                      onLogin();
                      onClose();
                    }}
                  >
                    Login
                  </button>
                </li>
                <li>
                  <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/signup" onClick={onClose}>
                    Signup
                  </Link>
                </li>
              </>
            )}

            <li>
              <Link className={`block px-3 py-2 rounded hover:bg-brand-50 ${isDeliveryMode ? 'text-gray-400 cursor-not-allowed opacity-50' : ''}`} to={isDeliveryMode ? '#' : "/dashboard"} onMouseEnter={isDeliveryMode ? undefined : () => preloadDashboard()} onFocus={isDeliveryMode ? undefined : () => preloadDashboard()} onClick={isDeliveryMode ? undefined : onClose}>
                Dashboard
              </Link>
            </li>

            {/* Affiliate Program - visible to all authenticated users */}
            {profile && notSuspended && (
              <li>
                <Link className={`block px-3 py-2 rounded hover:bg-brand-50 ${isDeliveryMode ? 'text-gray-400 cursor-not-allowed opacity-50' : ''}`} to={isDeliveryMode ? '#' : "/affiliate"} onClick={isDeliveryMode ? undefined : onClose}>
                  💰 Affiliate Program
                </Link>
              </li>
            )}

            {/* NEW: Reports (signed-in, not suspended) */}
            {profile && notSuspended && (
              <li>
                <Link className={`block px-3 py-2 rounded hover:bg-brand-50 ${isDeliveryMode ? 'text-gray-400 cursor-not-allowed opacity-50' : ''}`} to={isDeliveryMode ? '#' : "/reports"} onMouseEnter={isDeliveryMode ? undefined : () => preloadReports()} onFocus={isDeliveryMode ? undefined : () => preloadReports()} onClick={isDeliveryMode ? undefined : onClose}>
                  Reports
                </Link>
              </li>
            )}

            {/* Stores (visible even when logged out; access enforced by route) */}
            <li>
              <Link className={`block px-3 py-2 rounded hover:bg-brand-50 ${isDeliveryMode ? 'text-gray-400 cursor-not-allowed opacity-50' : ''}`} to={isDeliveryMode ? '#' : "/store"} onMouseEnter={isDeliveryMode ? undefined : () => preloadStores()} onFocus={isDeliveryMode ? undefined : () => preloadStores()} onClick={isDeliveryMode ? undefined : onClose}>
                🛍️ Stores
              </Link>
            </li>

            {/* Services Marketplace (visible to everyone) */}
            <li>
              <Link className={`block px-3 py-2 rounded hover:bg-brand-50 ${isDeliveryMode ? 'text-gray-400 cursor-not-allowed opacity-50' : ''}`} to={isDeliveryMode ? '#' : "/service-marketplace"} onClick={isDeliveryMode ? undefined : onClose}>
                🛠️ Services
              </Link>
            </li>

            {/* Apply for Store - visible to authenticated members only, but not to existing store managers */}
            {profile && notSuspended && !isStoreManager && (
              <li>
                <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/store-application" onClick={onClose}>
                  🏪 Apply for Store
                </Link>
              </li>
            )}

            {/* Order Processing - visible to packers, admins, and financial officers */}
            {profile && notSuspended && (typeof isPacker === 'function' ? isPacker() : false || isAdmin || isTreasurer || isManager) && (
              <li>
                <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/order-processing" onClick={onClose}>
                  📦 Order Processing
                </Link>
              </li>
            )}

            {/* Store Management - visible to store managers and admins */}
            {profile && notSuspended && (coopRole === 'admin' || isStoreManager) && (
              <li>
                <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/store-manager" onClick={onClose}>
                  Store Management
                </Link>
              </li>
            )}



                         {/* Delivery riders - from website users */}
             {isDelivery && notSuspended && (
               <li>
                 <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/rider/job-claim" onClick={onClose}>
                   Rider Jobs
                 </Link>
               </li>
             )}

            {/* Deliveries - visible to cashiers and treasurers */}
            {profile && notSuspended && (coopRole === 'cashier' || coopRole === 'treasurer') && (
              <li>
                <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/delivery-management" onClick={onClose}>
                  Deliveries
                </Link>
              </li>
            )}

            {/* Treasurer Payments - only visible to treasurers */}
            {profile && notSuspended && isTreasurer && (
              <li>
                <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/treasurer-payments" onClick={onClose}>
                  Treasurer Payments
                </Link>
              </li>
            )}


            {/* Admin - visible to admins */}
            {profile && coopRole === 'admin' && notSuspended && (
              <li>
                <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/admin/coop-membership" onClick={onClose}>
                  Admin
                </Link>
              </li>
            )}



            {/* Accounting - visible to treasurers and managers */}
            {profile && notSuspended && (isTreasurer || isManager) && !isDeliveryOnly && (
              <li>
                <Link className="block px-3 py-2 rounded hover:bg-brand-50" to="/accounting" onClick={onClose}>
                  Accounting
                </Link>
              </li>
            )}
          </ul>

          {profile && (
            <div className="mt-4 border-t pt-3 px-1">
              <div className="px-2 text-xs text-ink/60 mb-2">
                Signed in as
                <br />
                <span className="font-medium text-ink">{buildDisplayName(profile)}</span>
              </div>
              <button
                className="w-full px-3 py-2 rounded text-rose-700 hover:bg-rose-50 text-left"
                                 onClick={async () => {
                   clearAllAccess(); // Clear admin password access
                   await onSignout();
                   onClose();
                 }}
              >
                Sign out
              </button>
            </div>
          )}
        </nav>
      </div>
    </div>
  );
}





// Wrapper component that uses delivery mode context
function AppContent() {
  const [notifOpen, setNotifOpen] = React.useState(false);
  const { user, loading: authLoading, signout } = useAuth();
  const { profile, loading: profileLoading } = useUserProfile();
  const { isDelivery: isDeliveryUser, isRider, isDeliveryPerson, loading: deliveryLoading } = useDeliveryRole();
  const { isDeliveryMode } = useDeliveryMode();
  
  // Global affiliate tracking - works across all pages
  const { affiliateCode, isValidating, getAffiliateCode, getAffiliateInfo } = useAffiliateTracking(user);
  
  // Always fetch cooperative member roles if user is authenticated
  // This ensures treasurer/cashier roles are available for delivery management
  const shouldFetchCoopRoles = !!profile && !deliveryLoading;
  const { coopRole, isAdmin, isFinancial } = useCoopMemberRole(shouldFetchCoopRoles);
  
  // Determine if we're still loading authentication state
  // This prevents the navigation from showing inconsistent states
  const isAuthStateLoading = authLoading || profileLoading;
  
  // Determine if user is actually authenticated and profile is loaded
  const isAuthenticated = !!user && !!profile && !isAuthStateLoading;
  
  // Fetch packer role from website users (like delivery roles)
  const packerRoleData = usePackerRole(!!profile);
  const isPacker = packerRoleData?.isPacker || (() => false);
  
  // Fetch affiliate role - check if user has affiliate profile
  const affiliateData = useAffiliate(user?.uid);
  const isAffiliate = !!affiliateData?.affiliate && !affiliateData?.loading;
  
  const [loginOpen, setLoginOpen] = React.useState(false);
  const [loginRedirect, setLoginRedirect] = React.useState(null);
  const nav = useNavigate();
  React.useEffect(() => {
    const handleOpenLogin = (event) => {
      setLoginOpen(true);
      if (event.detail?.redirect) {
        setLoginRedirect(event.detail.redirect);
      }
    };
    window.addEventListener('ppac:open-login', handleOpenLogin);
    return () => window.removeEventListener('ppac:open-login', handleOpenLogin);
  }, []);
  
  React.useEffect(() => {
    if (loginOpen && user && !authLoading) {
      setLoginOpen(false);
      if (loginRedirect) {
        nav(loginRedirect, { replace: true });
        setLoginRedirect(null);
      }
    }
  }, [loginOpen, user, authLoading, loginRedirect, nav]);
  
  // Handle Google Sign-In redirect result (popup fallback)
  React.useEffect(() => {
    let isProcessing = false;
    
    const handleAuthRedirect = async () => {
      if (isProcessing) return;
      
      // Check if we're on an auth handler page
      const currentUrl = window.location.href;
      const isAuthHandler = currentUrl.includes('__/auth/handler');
      const isAuthIframe = currentUrl.includes('__/auth/iframe');
      
      if (isAuthHandler || isAuthIframe) {
        isProcessing = true;
        console.log("On auth handler page, processing redirect...");
        console.log("Current URL:", currentUrl);
        
        try {
          // Wait for Firebase Auth to process the redirect
          // This is critical - we need to wait long enough for Firebase to complete
          console.log("Waiting for Firebase Auth to process redirect...");
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
          
          // Try to get the redirect result
          const result = await handleGoogleRedirectResult();
          
          if (result) {
            console.log("✓ Google Sign-In successful! User:", result.user.email);
          } else {
            console.log("No redirect result, but checking auth state...");
            // Check if Firebase has already processed it and user is signed in
            if (auth.currentUser) {
              console.log("✓ User already signed in:", auth.currentUser.email);
            } else {
              console.log("✗ No user signed in yet");
            }
          }
        } catch (error) {
          console.error("Error processing redirect:", error);
        }
        
        // Now redirect to home
        console.log("Redirecting to home page...");
        window.location.replace('/');
      }
    };
    
    handleAuthRedirect();
  }, []); // Run only once on mount
  
  // Admin password access management
  const {
    hasAccountingAccess,
    hasReportsAccess,
    hasPaymentsAccess,
    grantAccountingAccess,
    grantReportsAccess,
    grantPaymentsAccess,
    clearAllAccess
  } = useAdminPasswordAccess();
  
  // Password modal states
  const [accountingPasswordModal, setAccountingPasswordModal] = React.useState(false);
  const [reportsPasswordModal, setReportsPasswordModal] = React.useState(false);
  const [paymentsPasswordModal, setPaymentsPasswordModal] = React.useState(false);

  // Admin index redirect component (defined inside App to access hooks)
  function AdminIndexRedirect() {
    try {
      // Only admins can access admin panel, always redirect to users
      return <Navigate to="users" replace />;
    } catch (error) {
      // Fallback to users page
      return <Navigate to="users" replace />;
    }
  }

  // PaymentsRedirect component for /payments route (defined inside App to access hooks)
  function PaymentsRedirect() {
    try {
      // Redirect based on role: treasurer/cashier go to store payments, others to member payments
      if (isFinancial) {
        return <Navigate to="/payments/store" replace />;
      } else {
        return <Navigate to="/payments/members" replace />;
      }
    } catch (error) {
      // Fallback to member payments
      return <Navigate to="/payments/members" replace />;
    }
  }

  // Use cooperative member roles from the new system
  // Only calculate roles if user is authenticated and has a role
  const isTreasurer = coopRole ? coopRole === 'treasurer' : false;
  const isCashier = coopRole ? coopRole === 'cashier' : false;
  // Treat both 'manager' and 'general manager' as managers for UI access
  // Also check for store manager roles in the profile
  const isManager = coopRole ? (coopRole === 'manager' || coopRole === 'general manager') : false;
  const isStoreManager = profile?.roles?.some(role => 
    role.startsWith('store_manager_') || 
    ['cooperative_store_manager', 'member_store_manager', 'establishment_store_manager'].includes(role)
  ) || false;
  const hasManagerAccess = isManager || isStoreManager;
  // Delivery roles come from website users, not from cooperative members
  const isDeliveryOnly = isDeliveryUser() && !isTreasurer && !hasManagerAccess;
  const notSuspended = profile?.suspended !== true;

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [riderSubMenuOpen, setRiderSubMenuOpen] = React.useState(false);
  // TECHNICAL OPTIMIZATION: Initialize performance optimizations
  React.useEffect(() => {
    // Initialize bundle optimizations immediately
    initializeBundleOptimizations();
    
    // Initialize security optimizations
    initializeSecurityOptimizations();
    
    // Prefetch common data after user is loaded
    if (user) {
      setTimeout(() => {
        prefetchFirestoreData();
      }, 3000); // Wait 3 seconds after user loads
    }
  }, [user]);

  React.useEffect(() => {
    // Initialize Firebase Messaging (foreground)
    initMessaging((payload) => {
      try {
        // Minimal foreground toast via alert; integrate with ToastProvider if desired
        const title = payload?.notification?.title || 'Notification';
        const body = payload?.notification?.body || '';
        // eslint-disable-next-line no-alert
        if (title || body) alert(`${title}\n${body}`);
      } catch {}
    });
  }, []);

  // Listen for admin access requests from mobile menu
  React.useEffect(() => {
    const handleAdminAccess = (event) => {
      if (event.detail === 'accounting') {
        setAccountingPasswordModal(true);
      }
    };

    window.addEventListener('admin-accounting-access', handleAdminAccess);
    return () => window.removeEventListener('admin-accounting-access', handleAdminAccess);
  }, []);

  // Listen for rider sub-menu requests
  React.useEffect(() => {
    const handleRiderMenu = () => {
      setRiderSubMenuOpen(true);
    };

    window.addEventListener('open-rider-menu', handleRiderMenu);
    return () => window.removeEventListener('open-rider-menu', handleRiderMenu);
  }, []);
  // Show the Admin mobile drawer trigger on Admin pages AND Payments pages
  const onAdminRelatedRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/payments");
  const onAccountingRoute = location.pathname.startsWith("/accounting");
  const onRiderRoute = location.pathname.startsWith("/rider");

  // Ensure fresh content when navigating back/forward from bfcache
  React.useEffect(() => {
    function onPageShow(e) {
      // If the page was restored from the bfcache, reload to fetch latest HTML/data
      if (e.persisted) {
        // Soft reload. Use location.replace to avoid double back entries.
        window.location.reload();
      }
    }
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Clear service worker caches on app load (but preserve auth data)
  React.useEffect(() => {
    // Only clear service worker caches, not localStorage/sessionStorage
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
      });
    }
    
    // Force reload if service worker is controlling the page
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
    }
  }, []);

  // Auto-apply mobile stack labels to tables across pages for a single responsive theme
  React.useEffect(() => {
    const enhanceTablesForMobile = () => {
      try {
        const tables = Array.from(document.querySelectorAll('table'));
        tables.forEach((table) => {
          const thead = table.querySelector('thead');
          const tbody = table.querySelector('tbody');
          if (!thead || !tbody) return;
          const headers = Array.from(thead.querySelectorAll('th')).map((th) => (th.textContent || '').trim());
          Array.from(tbody.querySelectorAll('tr')).forEach((tr) => {
            Array.from(tr.children).forEach((cell, idx) => {
              if (cell.tagName !== 'TD') return;
              if (!cell.getAttribute('data-label')) {
                const label = headers[idx] || '';
                if (label) cell.setAttribute('data-label', label);
              }
            });
          });
          // Ensure the table uses the mobile stack style
          if (!table.classList.contains('stack-on-mobile')) table.classList.add('stack-on-mobile');
        });
      } catch {}
    };

    // Defer to after paint to allow lazy routes to render
    const id = window.requestAnimationFrame(() => setTimeout(enhanceTablesForMobile, 0));
    return () => window.cancelAnimationFrame(id);
    // Re-run on route changes
  }, [location.pathname]);

  return (
    <ToastProvider>
      <StatusBarStrip color="#047857" heightPx={28} applyBodyPadding />
      <DeliveryModeBanner />
      <OfflineBanner />
      <A2HSPrompt />
      <PerformanceDashboard enabled={process.env.NODE_ENV === 'development'} />
      <PerformanceMonitor enabled={process.env.NODE_ENV === 'development'} />
      <DeliveryModeRouteGuard>
        <div className="min-h-screen text-ink -mt-1">
        {/* Top info bar (desktop only) */}
      <div className="hidden sm:flex w-full bg-brand-50 border-b border-brand-100 text-xs text-ink/70 py-1 px-2 items-center justify-between gap-2">
        <div className="flex gap-6">
          <span>📍 Agri Trading Center, Brgy. Irawan, Puerto Princesa City, Palawan 5300</span>
          <span>☎️ 0950-468-6668</span>
        </div>
        <div>
          <a
            href="https://facebook.com/ppac.coop"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-700 hover:underline flex items-center gap-1"
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.406.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.406 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
            </svg>
            Facebook
          </a>
        </div>
      </div>

  {/* Responsive header with mobile hamburger */}
      <header className="fixed left-0 right-0 z-[60] bg-white/95 backdrop-blur-sm border-b border-border" style={{top: 'var(--status-bar-height, 0px)'}}>
        <div className="mx-auto page-boxed page-gutter h-16 flex items-center justify-between md:pl-0">
          {/* Mobile back button inside header row */}
          <div className="md:hidden mr-2 -ml-2">
            <MobileBackButton fixed={false} />
          </div>
          <Link to="/" className="flex items-center gap-3">
            <img src={ppacLogo} alt="Puerto Princesa Agriculture Cooperative" className="h-8 sm:h-9 w-auto rounded-full" />
            <span className="text-base sm:text-xl font-semibold leading-tight truncate max-w-[52vw] sm:max-w-none">
              <span className="md:hidden">Merkado Ekspres</span>
              <span className="hidden md:inline">Puerto Princesa Agriculture Cooperative</span>
            </span>
          </Link>

          {/* Desktop nav - Responsive Navigation */}
          <div className="hidden md:flex items-center gap-4 flex-1 justify-between">
            <DeliveryModeToggle />
            
            <ResponsiveNavigation className="flex-1" maxWidth="calc(100vw - 400px)">
              <NavItem to="/" disabled={isDeliveryMode}>Home</NavItem>
              
              {isAuthStateLoading && (
                <div className="px-3 py-2 text-sm text-ink/50">
                  Loading...
                </div>
              )}
              
              {!isAuthStateLoading && !isAuthenticated && (
                <>
                  <button
                    className="px-3 py-2 rounded-lg text-sm font-medium transition text-ink/70 hover:bg-brand-50 hover:text-ink whitespace-nowrap"
                    style={{ background: loginOpen ? "#e0f2fe" : undefined }}
                    onClick={() => setLoginOpen(true)}
                  >
                    Login
                  </button>
                  <NavItem to="/signup" disabled={isDeliveryMode}>Signup</NavItem>
                </>
              )}
              
              <NavItem to="/dashboard" disabled={isDeliveryMode} onMouseEnter={() => preloadDashboard()} onFocus={() => preloadDashboard()}>Dashboard</NavItem>

              {/* Affiliate Program - visible to all authenticated users */}
              {!isAuthStateLoading && isAuthenticated && notSuspended && (
                <NavItem to="/affiliate" disabled={isDeliveryMode}>💰 Affiliate Program</NavItem>
              )}

              {/* Reports tab visible to any signed-in not-suspended member; access enforced inside route */}
              {!isAuthStateLoading && isAuthenticated && notSuspended && (
                <NavItem to="/reports" disabled={isDeliveryMode} onMouseEnter={() => preloadReports()} onFocus={() => preloadReports()}>Reports</NavItem>
              )}
              
              {/* Stores - visible to everyone (logged in or not) */}
              <NavItem to="/store" disabled={isDeliveryMode} onMouseEnter={() => preloadStores()} onFocus={() => preloadStores()}>🛍️ Stores</NavItem>

              {/* Services Marketplace - visible to everyone */}
              <NavItem to="/service-marketplace" disabled={isDeliveryMode}>🛠️ Services</NavItem>

              {/* Apply for Store - visible to authenticated members only, but not to existing store managers */}
              {!isAuthStateLoading && isAuthenticated && notSuspended && !isStoreManager && (
                <NavItem to="/store-application" disabled={isDeliveryMode}>🏪 Apply for Store</NavItem>
              )}
              
              {/* Order Processing - visible to packers, admins, and financial officers */}
              {!isAuthStateLoading && isAuthenticated && notSuspended && (isPacker() || isAdmin() || isFinancial()) && (
                <NavItem to="/order-processing" disabled={isDeliveryMode}>📦 Order Processing</NavItem>
              )}
              
              {/* Quick access for delivery riders - from website users */}
              {!isAuthStateLoading && isAuthenticated && isDeliveryUser() && notSuspended && (
                <NavItem to="/rider/job-claim" disabled={isDeliveryMode} onMouseEnter={() => preloadJobClaim()} onFocus={() => preloadJobClaim()}>
                  Rider Jobs
                </NavItem>
              )}

              {/* Delivery Management for cashiers and treasurers */}
              {!isAuthStateLoading && isAuthenticated && notSuspended && (coopRole === 'cashier' || coopRole === 'treasurer') && (
                <NavItem to="/delivery-management" disabled={isDeliveryMode}>Deliveries</NavItem>
              )}

              {/* Rider Management for cashiers and treasurers */}
              {!isAuthStateLoading && isAuthenticated && notSuspended && (coopRole === 'cashier' || coopRole === 'treasurer') && (
                <div className="relative group">
                  <button className="px-3 py-2 rounded-lg text-sm font-medium transition text-ink/70 hover:bg-brand-50 hover:text-ink flex items-center gap-1 whitespace-nowrap">
                    Rider Manage
                    <svg className="w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <NavLink 
                      to="/admin/rider-wallets" 
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                    >
                      Rider Wallets
                    </NavLink>
                    <NavLink 
                      to="/admin/rider-analytics" 
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
                    >
                      Rider Analytics
                    </NavLink>
                  </div>
                </div>
              )}

              {/* Treasurer Payments - only visible to treasurers */}
              {!isAuthStateLoading && isAuthenticated && notSuspended && coopRole === 'treasurer' && (
                <NavItem to="/treasurer-payments" disabled={isDeliveryMode} onMouseEnter={() => preloadTreasurerPayments()} onFocus={() => preloadTreasurerPayments()}>
                  Treasurer Payments
                </NavItem>
              )}

              {!isAuthStateLoading && isAuthenticated && coopRole === 'admin' && notSuspended && (
                <NavItem to="/admin/coop-membership" disabled={isDeliveryMode}>Admin</NavItem>
              )}

              {/* Store Management - visible to store managers and admins */}
              {!isAuthStateLoading && isAuthenticated && notSuspended && 
               (coopRole === 'admin' || isStoreManager) && (
                <NavItem to="/store-manager" disabled={isDeliveryMode}>Store Management</NavItem>
              )}

              {!isAuthStateLoading && isAuthenticated && (notSuspended && (isTreasurer || isManager) && !isDeliveryOnly) && (
                <NavItem to="/accounting">Accounting</NavItem>
              )}
              
              {!isAuthStateLoading && isAuthenticated && coopRole === 'admin' && notSuspended && (
                <button
                  onClick={() => setAccountingPasswordModal(true)}
                  className="px-3 py-2 rounded-lg text-sm font-medium transition text-ink/70 hover:bg-brand-50 hover:text-ink whitespace-nowrap"
                >
                  Accounting (Admin)
                </button>
              )}
            </ResponsiveNavigation>
            
            {/* User Profile Section */}
            {!isAuthStateLoading && isAuthenticated && (
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Enable Notifications (desktop only) */}
                {typeof window !== 'undefined' && 'Notification' in window && Notification.permission !== 'granted' && (
                  <button
                    className="px-3 py-2 rounded-lg text-sm font-medium transition text-ink/70 hover:bg-brand-50 hover:text-ink whitespace-nowrap"
                    onClick={async () => {
                      try {
                        const res = await fcmService.requestBrowserPermission();
                        if (res?.granted) {
                          alert('Notifications enabled. Thank you!');
                        } else {
                          alert('Notifications are blocked or were dismissed. You can enable them in your browser settings.');
                        }
                      } catch {}
                    }}
                  >
                    Enable notifications
                  </button>
                )}
                {/* Profile Picture */}
                {profile.profilePhotoURL && (
                  <img 
                    src={profile.profilePhotoURL} 
                    alt="Profile" 
                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                  />
                )}
                
                {/* Welcome Message */}
                <span className="text-sm text-ink/80 whitespace-nowrap hidden lg:inline">
                  Welcome, {buildDisplayName(profile)}
                </span>
                
                {/* Sign Out Button */}
                <button
                  className="px-3 py-2 rounded-lg text-sm font-medium transition text-rose-700 hover:bg-rose-50 hover:text-rose-900 whitespace-nowrap"
                  onClick={async () => {
                    if (window.confirm("Are you sure you want to sign out?")) {
                      clearAllAccess(); // Clear admin password access
                      await signout();
                      nav("/", { replace: true });
                    }
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>

          {/* Mobile actions: optional Admin menu + main hamburger */}
          <div className="md:hidden flex items-center gap-2">
            {/* PWA Install Button */}
            <button 
              id="install-app-btn" 
              className="hidden inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium shadow-sm hover:bg-gray-50"
              onClick={() => window.__promptInstall?.()}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18l0-10" />
              </svg>
              Install App
            </button>
            {!isAuthStateLoading && isAuthenticated && (onAdminRelatedRoute && coopRole === 'admin') && (
              <button
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium shadow-sm"
                aria-label="Open admin menu"
                onClick={() => {
                  // Notify Admin layout to open its drawer
                  window.dispatchEvent(new Event("open-admin-menu"));
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>Menu</span>
              </button>
            )}

            {onAccountingRoute && (
              <button
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium shadow-sm"
                aria-label="Open accounting menu"
                onClick={() => {
                  window.dispatchEvent(new Event("open-accounting-menu"));
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M4 7h16M8 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>Menu</span>
              </button>
            )}

            {onRiderRoute && (
              <button
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium shadow-sm"
                aria-label="Open jobs menu"
                onClick={() => {
                  window.dispatchEvent(new Event("open-rider-menu"));
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>Menu</span>
              </button>
            )}

            <button className="inline-flex items-center justify-center p-2 rounded-lg hover:bg-brand-50" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
          </div>
        </div>
      </header>

      {/* MobileMenu rendered outside header for full viewport coverage */}
             <MobileMenu
         open={menuOpen}
         onClose={() => setMenuOpen(false)}
         isAdmin={!profileLoading && coopRole === 'admin'}
         isDelivery={!profileLoading && isDeliveryUser()}
         isTreasurer={!profileLoading && isTreasurer}
         isCashier={!profileLoading && isCashier}
         isManager={!profileLoading && hasManagerAccess}
         isStoreManager={!profileLoading && isStoreManager}
         isPacker={!profileLoading && isPacker()}
         isDeliveryOnly={!profileLoading && isDeliveryOnly}
         notSuspended={!profileLoading && notSuspended}
         profile={profile}
         coopRole={coopRole}
         clearAllAccess={clearAllAccess}
         isDeliveryMode={isDeliveryMode}
         onLogin={() => setLoginOpen(true)}
         onSignup={() => nav("/signup")}
        onSignout={async () => {
           clearAllAccess(); // Clear admin password access
           await signout();
            setLoginRedirect(null);
           nav("/", { replace: true });
         }}
      />

      {/* Rider Sub-Menu for rider routes */}
      <RiderSubMenu 
        open={riderSubMenuOpen} 
        onClose={() => setRiderSubMenuOpen(false)} 
      />

      <LoginModal
        open={loginOpen}
        onClose={() => {
          setLoginOpen(false);
          setLoginRedirect(null);
        }}
        onSuccess={() => {
          setLoginOpen(false);
          if (loginRedirect) {
            nav(loginRedirect, { replace: true });
            setLoginRedirect(null);
          }
        }}
      />

      <div className="min-h-screen text-ink">
        <main className="pb-[calc(5rem+env(safe-area-inset-bottom))] sm:pb-10" style={{paddingTop: 'calc(var(--status-bar-height, 0px) + 4rem)'}}>
        <Routes>
          {/* Home (driven by Firestore via pages/Home.jsx) */}
          <Route path="/" element={<Home />} />

          {/* Firebase Auth Handler - for OAuth redirects */}
          <Route 
            path="/__/auth/handler" 
            element={
              <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-ink/70">Completing sign-in...</p>
                </div>
              </div>
            } 
          />

          {/* Standalone pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/account-deletion" element={<AccountDeletion />} />
                  {/* Become Member Routes */}
          <Route path="/become-member" element={<BecomeMember />} />
          <Route path="/BecomeMember" element={<Navigate to="/become-member" replace />} />
          <Route path="/become-member/apply" element={<Navigate to="/membership-application" replace />} />
          <Route path="/become-member/benefits" element={<Navigate to="/become-member" replace />} />
        <Route path="/RequirementsMembership" element={<RequirementsMembership />} />
        <Route
          path="/mobile-app-login"
          element={
            <Suspense fallback={<div className="p-6 text-center">Preparing your form...</div>}>
              <MobileDashboardLogin />
            </Suspense>
          }
        />
        <Route path="/membership-application" element={<MembershipApplication />} />

          {/* Guest-only */}
          <Route
            path="/signup"
            element={
              <GuestRoute>
                <Signup openLoginModal={() => setLoginOpen(true)} />
              </GuestRoute>
            }
          />
          <Route
            path="/reset"
            element={
              <GuestRoute>
                <Reset />
              </GuestRoute>
            }
          />

          {/* Auth-only */}
          <Route
            path="/verify"
            element={
              <ProtectedRoute>
                <Verify />
              </ProtectedRoute>
            }
          />
          {/* Firebase action handler for email verification */}
          <Route
            path="/__/auth/action"
            element={<VerifyAction />}
          />
          {/* Alternative Firebase action URL format */}
          <Route
            path="/auth/action"
            element={<VerifyAction />}
          />
          {/* OAuth handler route for server-side Google OAuth (id_token in hash) */}
          <Route path="/oauth-handler" element={<OAuthHandler />} />
          <Route
            path="/dashboard"
            element={<DashboardRouteElement />}
          />

          {/* Affiliate Program */}
          <Route
            path="/affiliate"
            element={
              <ProtectedRoute>
                <AffiliatePage />
              </ProtectedRoute>
            }
          />

          {/* Stores public routes */}
          <Route path="/store" element={<Stores />} />
          <Route path="/store/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<CartPage />} />
          
          {/* Services Marketplace routes */}
          <Route path="/service-marketplace/*" element={<ServiceMarketplace />} />
          
          {/* App Download Route */}
          <Route path="/download" element={<DownloadApp />} />
          
          {/* Store Application Route */}
          <Route 
            path="/store-application" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <StoreApplication />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Store Manager Route */}
          <Route 
            path="/store-manager" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <StoreManager />
                </Suspense>
              </ProtectedRoute>
            } 
          />

          {/* Store Wallet Route */}
          <Route 
            path="/store-wallet" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <StoreWallet />
                </Suspense>
              </ProtectedRoute>
            } 
          />

          {/* Checkout route */}
          <Route path="/checkout" element={<Checkout />} />

                     {/* Main payments route - redirects based on user role */}
           <Route
             path="/payments"
             element={
               <ProtectedRoute>
                 <RequireRole 
                   allowed={["treasurer","cashier","member"]}
                   coopRole={coopRole}
                   isAdmin={isAdmin()}
                   isFinancial={isFinancial()}
                 >
                   <Suspense fallback={<div className="p-6">Loading…</div>}>
                     <PaymentsRedirect />
                   </Suspense>
                 </RequireRole>
               </ProtectedRoute>
             }
           />
                     {/* New payments pages */}
           <Route
             path="/payments/members"
             element={
               <ProtectedRoute>
                 <RequireRole 
                   allowed={["treasurer","cashier","member"]}
                   coopRole={coopRole}
                   isAdmin={isAdmin()}
                   isFinancial={isFinancial()}
                 >
                   <Suspense fallback={<div className="p-6">Loading…</div>}>
                     <MemberPayments />
                   </Suspense>
                 </RequireRole>
               </ProtectedRoute>
             }
           />
           <Route
             path="/payments/store"
             element={
               <ProtectedRoute>
                 <RequireRole 
                   allowed={["treasurer","cashier"]}
                   coopRole={coopRole}
                   isAdmin={isAdmin()}
                   isFinancial={isFinancial()}
                 >
                   <Suspense fallback={<div className="p-6">Loading…</div>}>
                     <StorePayments />
                   </Suspense>
                 </RequireRole>
               </ProtectedRoute>
             }
           />

           {/* Treasurer Payments - only visible to treasurers */}
           <Route
             path="/treasurer-payments"
             element={
               <ProtectedRoute>
                 <RequireRole 
                   allowed={["treasurer"]} 
                   coopRole={coopRole}
                   isAdmin={isAdmin()}
                   isFinancial={isFinancial()}
                 >
                   <Suspense fallback={<div className="p-6">Loading…</div>}>
                     <TreasurerPayments />
                   </Suspense>
                 </RequireRole>
               </ProtectedRoute>
             }
           />
                     {/* Rider jobs menu (nested) - Delivery roles from website users */}
           <Route
             path="/rider"
             element={
               <ProtectedRoute>
                 <RequireRole 
                   allowed={["delivery","rider","cashier"]}
                   coopRole={coopRole}
                   isAdmin={isAdmin()}
                   isFinancial={isFinancial()}
                 >
                   <Suspense fallback={<div className="p-6">Loading…</div>}>
                     <RiderLayout />
                   </Suspense>
                 </RequireRole>
               </ProtectedRoute>
             }
           >
            <Route index element={<Navigate to="job-claim" replace />} />
            <Route path="job-claim" element={<Suspense fallback={<div className="p-6">Loading…</div>}><JobClaim /></Suspense>} />
            <Route path="my-jobs" element={<Suspense fallback={<div className="p-6">Loading…</div>}><MyJobs /></Suspense>} />
            <Route path="wallet" element={<Suspense fallback={<div className="p-6">Loading…</div>}><RiderWallet /></Suspense>} />
            <Route path="analytics" element={<Suspense fallback={<div className="p-6">Loading…</div>}><RiderAnalytics /></Suspense>} />
          </Route>
                     {/* Backward-compatible redirect from old route */}
           <Route path="/rider/jobs" element={<Navigate to="/rider/job-claim" replace />} />
          {/* legacy /my-deliveries route removed (use /rider/jobs) */}
          <Route
            path="/orders"
            element={
              <ProtectedRoute redirectToIfUnauthed="/store">
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          
          {/* Order Processing - for packers, admins, and financial officers */}
          <Route
            path="/order-processing"
            element={
              <ProtectedRoute>
                <RequireRole 
                  allowed={["packer", "admin", "treasurer", "cashier", "manager", "general manager"]}
                  coopRole={coopRole}
                  isAdmin={isAdmin()}
                  isFinancial={isFinancial()}
                >
                  <Suspense fallback={<div className="p-6">Loading…</div>}>
                    <OrderProcessing />
                  </Suspense>
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route
            path="/receipt/:paymentId"
            element={
              <ProtectedRoute>
                <ReceiptPage />
              </ProtectedRoute>
            }
          />
          {/* gateway routes removed */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

                     {/* NEW: Reports (full members only, except treasurer always allowed) */}
           <Route
             path="/reports"
             element={
               <ProtectedRoute>
                 <RequireFullMemberOrRole rolesAllowed={["treasurer"]}>
                   <Suspense fallback={<div className="p-6">Loading…</div>}>
                     <Reports />
                   </Suspense>
                 </RequireFullMemberOrRole>
               </ProtectedRoute>
             }
           />
           <Route
             path="/reports/:id"
             element={
               <ProtectedRoute>
                 <RequireFullMemberOrRole rolesAllowed={["treasurer"]}>
                   <Suspense fallback={<div className="p-6">Loading…</div>}>
                     <ReportView />
                   </Suspense>
                 </RequireFullMemberOrRole>
               </ProtectedRoute>
             }
           />

       {/* Admin/Delivery (nested). Admin full access; delivery uses Orders only.
         Keep route guard inclusive: admin OR delivery to access /admin; fine-grained pages self-limit below.
       */}
          {/** Role-aware index redirect component for /admin */}

                     <Route
             path="/admin"
             element={
               <ProtectedRoute>
                                 <RequireRole 
                  allowed={["admin"]}
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <React.Suspense fallback={<div className="p-6">Loading admin...</div>}>
                    <AdminLayout />
                  </React.Suspense>
                </RequireRole>
               </ProtectedRoute>
             }
           >
            <Route index element={<AdminIndexRedirect />} />
            <Route
              path="users"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <AdminUsers />
                </RequireRole>
              }
            />
            <Route
              path="edit-home"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <EditHome />
                </RequireRole>
              }
            />

            
            <Route
              path="cooperative-store"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <CooperativeStoreManager />
                </RequireRole>
              }
            />
                         <Route
               path="deliveries"
               element={
                 <RequireRole 
                   allowed={["admin"]} 
                   fallback="/"
                   coopRole={coopRole}
                   isAdmin={isAdmin}
                   isFinancial={isFinancial}
                 >
                   <DeliveryManagement />
                 </RequireRole>
               }
             />
            <Route
              path="settings/accounting"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <AccountingSettingsPage />
                </RequireRole>
              }
            />
            <Route
              path="ops"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <OpsDashboard />
                </RequireRole>
              }
            />
            <Route
              path="coop-membership"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <CoopMembership />
                </RequireRole>
              }
            />
            <Route
              path="store-management"
              element={
                <RequireRole 
                  allowed={["admin", "cooperative_store_manager", "member_store_manager", "establishment_store_manager"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <StoreManagementPage />
                </RequireRole>
              }
            />
            <Route
              path="service-management"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <ServiceManagement />
                </RequireRole>
              }
            />
            <Route
              path="store-settings"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <StoreSettings />
                </RequireRole>
              }
            />
            <Route
              path="cash-out-management"
              element={
                <RequireRole 
                  allowed={["admin", "treasurer", "cashier"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <CashOutManagement />
                </RequireRole>
              }
            />
            <Route
              path="store-applications"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading…</div>}>
                    <StoreApplications />
                  </Suspense>
                </RequireRole>
              }
            />
            <Route
              path="store-orders"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading…</div>}>
                    <StoreOrdersManagement />
                  </Suspense>
                </RequireRole>
              }
            />
            <Route
              path="product-affiliate-settings"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading…</div>}>
                    <ProductAffiliateSettings />
                  </Suspense>
                </RequireRole>
              }
            />
            <Route
              path="discounts"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <DiscountManagement />
                </RequireRole>
              }
            />
            <Route
              path="promotions"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading…</div>}>
                    <PromotionManagement />
                  </Suspense>
                </RequireRole>
              }
            />
            <Route
              path="delivery-settings"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading...</div>}>
                    <DeliverySettings />
                  </Suspense>
                </RequireRole>
              }
            />
            <Route
              path="fcm"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading...</div>}>
                    <FCMMessaging />
                  </Suspense>
                </RequireRole>
              }
            />
            <Route
              path="daily-cooperative-store-sales"
              element={
                <RequireRole 
                  allowed={["admin", "treasurer", "staff"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading...</div>}>
                    <DailyCooperativeStoreSales />
                  </Suspense>
                </RequireRole>
              }
            />
            <Route
              path="rider-wallets"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <RiderWallets />
                </RequireRole>
              }
            />
            <Route
              path="rider-analytics"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <AdminRiderAnalytics />
                </RequireRole>
              }
            />
            <Route
              path="apk-management"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading...</div>}>
                    <APKManagement />
                  </Suspense>
                </RequireRole>
              }
            />
            <Route
              path="qr-codes"
              element={
                <RequireRole 
                  allowed={["admin"]} 
                  fallback="/"
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading...</div>}>
                    <QRCodeManagement />
                  </Suspense>
                </RequireRole>
              }
            />

          </Route>

                     {/* Accounting-only (nested) - Officers only, or admins with password */}
           <Route
       path="/accounting"
             element={
               <ProtectedRoute>
                 <AccountingRouteGuard 
                   coopRole={coopRole} 
                   hasAccountingAccess={hasAccountingAccess}
                   onShowPasswordModal={() => setAccountingPasswordModal(true)}
                 />
               </ProtectedRoute>
             }
           >
            <Route index element={<Navigate to="journal-entries" replace />} />
            <Route
              path="chart-of-accounts"
              element={
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <ChartOfAccounts />
                </Suspense>
              }
            />
            <Route
              path="journal-entries"
              element={
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <JournalEntries />
                </Suspense>
              }
            />
            <Route
              path="general-journal"
              element={
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <GeneralJournal />
                </Suspense>
              }
            />
            <Route
              path="ledger"
              element={
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <Ledger />
                </Suspense>
              }
            />
            <Route
              path="trial-balance"
              element={
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <TrialBalance />
                </Suspense>
              }
            />
            <Route
              path="financial-statements"
              element={
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <FinancialStatements />
                </Suspense>
              }
            />
          </Route>

          {/* Cashier/Treasurer Delivery Management */}
          <Route
            path="/delivery-management"
            element={
              <ProtectedRoute>
                <RequireRole 
                  allowed={["cashier", "treasurer"]}
                  coopRole={coopRole}
                  isAdmin={isAdmin}
                  isFinancial={isFinancial}
                >
                  <Suspense fallback={<div className="p-6">Loading delivery management...</div>}>
                    <DeliveryManagement />
                  </Suspense>
                </RequireRole>
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route
            path="*"
            element={
              <div className="text-center py-20">
                <h2 className="text-3xl font-bold mb-2">404 — Page Not Found</h2>
                <p className="text-ink/70">Check the URL or use the navigation above.</p>
              </div>
            }
          />
        </Routes>
                 {/* Mobile bottom tab bar */}
                  <MobileTabBar
                         showRider={!profileLoading && isDeliveryUser() && notSuspended}
            showAdmin={!profileLoading && coopRole === 'admin' && notSuspended}
            showAccounting={!profileLoading && notSuspended && (isTreasurer || isManager) && !isDeliveryOnly}
            showDeliveryManagement={(() => {
              // Only show if we have a definitive answer (role loaded) and user has permission
              const roleLoaded = coopRole !== null && coopRole !== undefined;
              return !profileLoading && notSuspended && roleLoaded && (coopRole === 'cashier' || coopRole === 'treasurer');
            })()}
            showAffiliate={!profileLoading && isAffiliate && notSuspended}
            onOpenMenu={() => setMenuOpen(true)}
            onOpenNotification={() => setNotifOpen(true)}
            // New props for smart bottom menu
            isStoreManager={!profileLoading && isStoreManager}
            isPacker={!profileLoading && isPacker()}
            isCashier={!profileLoading && coopRole === 'cashier'}
            isTreasurer={!profileLoading && isTreasurer}
            isManager={!profileLoading && isManager}
            isDelivery={!profileLoading && isDeliveryUser()}
          />
                 <NotificationModal open={notifOpen} onClose={() => setNotifOpen(false)} />
         
         {/* Admin Password Modals */}
         <AdminPasswordModal
           open={accountingPasswordModal}
           onClose={() => setAccountingPasswordModal(false)}
           onSuccess={() => {
             grantAccountingAccess();
             setAccountingPasswordModal(false);
             nav('/accounting');
           }}
           title="Admin Access to Accounting"
           message="Enter admin password to access the accounting area"
         />
        </main>
      </div>

      <footer className="border-t border-border bg-white/70 backdrop-blur">
        {/* Info bar moved to footer on mobile */}
        <div className="sm:hidden bg-brand-50 border-b border-brand-100 text-xs text-ink/70">
          <div className="mx-auto max-w-5xl px-4 py-2 space-y-1">
            <div>📍 Agri Trading Center, Brgy. Irawan, Puerto Princesa City, Palawan 5300</div>
            <div>☎️ 0950-468-6668</div>
            <div>
              <a
                href="https://facebook.com/ppac.coop"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 hover:underline inline-flex items-center gap-1"
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.406.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.406 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
                </svg>
                Facebook
              </a>
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-5xl px-4 py-6 text-sm text-ink/60">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <div>© {new Date().getFullYear()} Puerto Princesa Agriculture Cooperative</div>
            <div className="flex space-x-4">
              <Link to="/privacy-policy" className="hover:text-ink/80 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="hover:text-ink/80 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
            </div>
        </DeliveryModeRouteGuard>
          </ToastProvider>
  );
}

// Main App component that provides the DeliveryModeProvider
export default function App() {
  return (
    <ErrorBoundary>
      <CartProvider>
        <DeliveryModeProvider>
          <AppContent />
        </DeliveryModeProvider>
      </CartProvider>
    </ErrorBoundary>
  );
}
